/**
 * 后端接口客户端。
 *
 * ════════════════════════════════════════════════════════
 * 和扩展那份（apps/extension/src/api.ts）的区别：**这边走 cookie。**
 * ════════════════════════════════════════════════════════
 * 扩展没得选，只能用 Bearer token —— 它是 `chrome-extension://` 源，
 * 跨源带 cookie 很难弄对。
 *
 * 网页版反过来：cookie 带 httpOnly，XSS 拿不到；而 localStorage 里的 token
 * 一次 XSS 就全丢。而且开发和生产都走同源（Vite 代理 / Caddy 转发），
 * 所以 SameSite 和 CORS 这两件事根本不会出现。
 *
 * 唯一的代价是要防 CSRF —— 那一层 better-auth 用 Origin 校验做了
 * （见 trustedOrigins），我们只要保证不把 API 暴露成跨源可调即可。
 */

export const API_BASE = '/api';

export type ApiError =
  /** 没登录或会话过期。**要跳登录页，不是提示重试** */
  | { kind: 'unauthorized' }
  /**
   * 乐观锁冲突：设置在别处被改过。
   * 单独一类是因为处理方式特殊 —— 要把最新值读回来让用户重新确认，
   * 而不是重试（重试会再撞一次）也不是报错了事（用户会以为自己保存成功了）。
   */
  | { kind: 'conflict'; currentVersion: number; conflictKeys: string[]; message: string }
  /** 配额不够，需要升级。前端要引导去订阅页而不是显示「出错了」 */
  | { kind: 'quota'; message: string; limit: number; used: number }
  /**
   * 字段级校验失败。problems 要显示在对应的字段上。
   *
   * `code` 里可能出现 `quota_exceeded` —— 那是「这个值超出你档位的上限」，
   * 界面要引导升级而不是说「填错了」。
   */
  | { kind: 'invalid'; problems: Array<{ key: string; code: string; message: string }>; message: string }
  | { kind: 'not_found'; message: string }
  | { kind: 'duplicate'; message: string; existingId?: string | undefined }
  | { kind: 'rate_limited'; retryAfterMs: number | undefined; code?: string | undefined }
  /**
   * 请求被拒。
   *
   * `code` 是服务端给的机器可读的原因（目前只有发验证码那一路在给，`OTP_*`）。
   * **有它才能把错误翻成用户的语言** —— `message` 是服务端写的简体，
   * 英文界面的用户看到一个中文错误，那一刻信任就没了。见 `lib/otp-errors.ts`。
   */
  | { kind: 'bad_request'; message: string; code?: string | undefined }
  | { kind: 'server_error'; message: string; reqId?: string | undefined }
  | { kind: 'network'; message: string }
  | { kind: 'timeout' }
  /**
   * 游客模式下碰了要登录的接口。**请求根本没发出去** —— 见 `setGuestMode`。
   *
   * 和 `unauthorized` 分开：那个是「本来登录着、会话过期了」，要重新登录；
   * 这个是「从来没登录过、正在随便看看」，要做的是**邀请**他登录，
   * 而不是告诉他「登录已失效」—— 对一个第一次来的人说这句话是错的。
   */
  | { kind: 'guest' };

export type Result<T> = { ok: true; data: T } | { ok: false; error: ApiError };

interface RawBody {
  error?: string;
  message?: string;
  reason?: string;
  problems?: Array<{ key?: string; path?: string; code?: string; message?: string }>;
  currentVersion?: number;
  conflictKeys?: string[];
  limit?: number;
  used?: number;
  existingId?: string;
  reqId?: string;
  /** 机器可读的原因。发验证码那一路会给（`OTP_*`），用来把错误翻成用户的语言 */
  code?: string;
}

/** 从响应体里挑一句能给用户看的话 */
function messageOf(b: RawBody | null, fallback: string): string {
  const m = b?.message ?? b?.error;
  return m !== undefined && m !== null && String(m).trim().length > 0 ? String(m) : fallback;
}

/* ══════════════════════ 游客模式 ══════════════════════ */

/**
 * 没登录也能逛：页面都能看、能改着玩，但除了登录和公开接口，一个请求都不发。
 *
 * ════════════════════════════════════════════════════════
 * 为什么在这一层拦，而不是让每个页面自己判断
 * ════════════════════════════════════════════════════════
 * 写操作散在十几个地方（设置保存、恢复默认、凭据、加钱包、加币、开关、删除…）。
 * 每个页面各自判断的话，漏一个就是一次白发的请求 —— 服务器会回 401，
 * 而页面把它显示成「登录已失效」，对一个第一次来的人说这句话是错的。
 * 在这里拦是**兜底**：页面该做的邀请登录照样做，漏掉的也不会真的发出去。
 *
 * **这不是安全边界。** 真正的边界在服务端（每个接口都要会话）。
 * 这一层只管体验：不发没用的请求、不说错话。
 */
let guestMode = false;

/** 进入 / 离开游客模式。由 AppProvider 在启动发现没登录、以及登录成功时调用 */
export function setGuestMode(on: boolean): void {
  guestMode = on;
}

export function isGuestMode(): boolean {
  return guestMode;
}

/**
 * 游客模式下也放行的接口。
 *
 *   `/auth/`    登录本身（发验证码、验证）—— 拦了它就永远登录不了
 *   `/public/`  公开信息（价目表）—— 服务端这组接口本来就不要会话
 *
 * 用前缀而不是列一张全名单：公开接口以后会加（功能介绍页要的东西），
 * 而全名单漏一个的表现是「那一块在游客模式下永远是空的」，不报错。
 */
export function isPublicPath(path: string): boolean {
  /* `/me` 也放行：「我到底登录了没有」这个问题游客模式下也必须能问。
     踩过一次 —— 游客登录成功之后重新启动，拦截还开着，`/me` 被拦成「要先登录」，
     刚登录的人看到一屏「连不上服务器」。放行它之后，
     就算启动流程哪天被改乱了顺序，这件事也不会再发生 */
  return path === '/me' || path.startsWith('/auth/') || path.startsWith('/public/') || path.startsWith('/dev/');
}

async function call<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
  opts: { timeoutMs?: number; headers?: Record<string, string> } = {}
): Promise<Result<T>> {
  if (guestMode && !isPublicPath(path)) return { ok: false, error: { kind: 'guest' } };

  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(), opts.timeoutMs ?? 20_000);

  let res: Response;
  try {
    res = await fetch(API_BASE + path, {
      method,
      signal: ac.signal,
      /* 同源也要写 same-origin：默认值在旧浏览器里是 omit，
         那会让「明明登录了却一直 401」这种问题冒出来 */
      credentials: 'same-origin',
      headers: {
        accept: 'application/json',
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        /* 额外的头放最后，但**不许覆盖上面两个** */
        ...(opts.headers ?? {})
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {})
    });
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return ac.signal.aborted
      ? { ok: false, error: { kind: 'timeout' } }
      : { ok: false, error: { kind: 'network', message: msg } };
  } finally {
    clearTimeout(timer);
  }

  let parsed: RawBody | null = null;
  let text = '';
  try {
    text = await res.text();
    parsed = text.length > 0 ? (JSON.parse(text) as RawBody) : null;
  } catch {
    parsed = null;
  }

  if (res.ok) return { ok: true, data: (parsed ?? null) as T };

  /* ── 按状态码 + reason 分类 ──
     分得细是为了让界面能**区别对待**：
     401 要跳登录、409 要重读、402 要引导升级、400 的 problems 要落到字段上。
     全部揉成「出错了」的话，用户只能猜。 */

  if (res.status === 401) return { ok: false, error: { kind: 'unauthorized' } };

  if (res.status === 429) {
    const ra = res.headers.get('retry-after');
    const secs = ra !== null ? Number(ra) : NaN;
    return {
      ok: false,
      error: {
        kind: 'rate_limited',
        retryAfterMs: Number.isFinite(secs) && secs > 0 ? secs * 1000 : undefined,
        ...(typeof parsed?.code === 'string' ? { code: parsed.code } : {})
      }
    };
  }

  if (res.status === 409 && parsed?.reason === 'version_conflict') {
    return {
      ok: false,
      error: {
        kind: 'conflict',
        currentVersion: Number(parsed.currentVersion ?? 0),
        conflictKeys: parsed.conflictKeys ?? [],
        message: messageOf(parsed, '设置在别处被改过了')
      }
    };
  }

  if (res.status === 409 && parsed?.reason === 'duplicate') {
    return {
      ok: false,
      error: {
        kind: 'duplicate',
        message: messageOf(parsed, '已经存在了'),
        ...(parsed.existingId ? { existingId: parsed.existingId } : {})
      }
    };
  }

  if (res.status === 402) {
    return {
      ok: false,
      error: {
        kind: 'quota',
        message: messageOf(parsed, '超出了你这个档位的上限'),
        limit: Number(parsed?.limit ?? 0),
        used: Number(parsed?.used ?? 0)
      }
    };
  }

  if (res.status === 404) {
    return { ok: false, error: { kind: 'not_found', message: messageOf(parsed, '找不到') } };
  }

  if (res.status === 400 && Array.isArray(parsed?.problems)) {
    return {
      ok: false,
      error: {
        kind: 'invalid',
        problems: parsed.problems.map(p => ({
          key: String(p.key ?? p.path ?? ''),
          code: String(p.code ?? 'invalid'),
          message: String(p.message ?? '这个值不合法')
        })),
        message: messageOf(parsed, '有几项填得不对')
      }
    };
  }

  if (res.status >= 500) {
    return {
      ok: false,
      error: {
        kind: 'server_error',
        message: messageOf(parsed, `服务器出错了（${res.status}）`),
        ...(parsed?.reqId ? { reqId: parsed.reqId } : {})
      }
    };
  }

  return {
    ok: false,
    error: {
      kind: 'bad_request',
      message: messageOf(parsed, `请求被拒绝（${res.status}）`),
      ...(typeof parsed?.code === 'string' ? { code: parsed.code } : {})
    }
  };
}

/** 给用户看的一句话。**绝不返回空字符串** */
export function describeError(e: ApiError): string {
  switch (e.kind) {
    case 'unauthorized':
      return '登录已失效，请重新登录';
    case 'conflict':
      return e.message;
    case 'quota':
      return e.message;
    case 'invalid':
      return e.message;
    case 'not_found':
      return e.message;
    case 'duplicate':
      return e.message;
    case 'rate_limited':
      return e.retryAfterMs !== undefined
        ? `操作太频繁了，${Math.ceil(e.retryAfterMs / 1000)} 秒后再试`
        : '操作太频繁了，稍后再试';
    case 'bad_request':
      return e.message;
    case 'server_error':
      /* 带上请求 id：用户截图给我们的时候，这一串能直接定位到那条日志 */
      return e.reqId ? `${e.message}（编号 ${e.reqId}）` : e.message;
    case 'network':
      return '连不上服务器，检查一下网络：' + e.message;
    case 'timeout':
      return '服务器响应超时，稍后再试';
    case 'guest':
      return '这一步要先登录。现在是游客浏览：页面都能看、能改着玩，但不会保存';
  }
}

/** 这个错误重试有没有意义 */
export function isRetryable(e: ApiError): boolean {
  /* unauthorized 要重新登录、conflict 要重读、invalid 和 quota 要改内容 ——
     这四种重试一万次结果都一样 */
  return e.kind === 'network' || e.kind === 'timeout' ||
    e.kind === 'server_error' || e.kind === 'rate_limited';
}

export const api = {
  get: <T>(path: string) => call<T>('GET', path),
  post: <T>(path: string, body?: unknown, opts?: { headers?: Record<string, string>; timeoutMs?: number }) =>
    call<T>('POST', path, body, opts ?? {}),
  patch: <T>(path: string, body?: unknown) => call<T>('PATCH', path, body),
  put: <T>(path: string, body?: unknown) => call<T>('PUT', path, body),
  del: <T>(path: string) => call<T>('DELETE', path)
};

/* ══════════════════════ 接口的返回形状 ══════════════════════ */

export interface Me {
  user: {
    id: string;
    name: string;
    email: string;
    emailVerified: boolean;
    image: string | null;
    createdAt: string;
    /** Telegram 登录的用户邮箱是我们造的占位值，要提示他补一个真邮箱 */
    emailIsPlaceholder: boolean;
  } | null;
  profile: {
    /* 这两个是服务端**算出来的生效值**（设置里的 tz / resetAt），
       不是某张表里的列 —— 账号页显示「你的今天从几点算起」靠它们。
       界面语言不在这里：它是设置项 `lang`，从 /api/settings 读。 */
    timezone: string;
    dayStartAt: string;
    referralCode: string;
    referredBy: string | null;
  } | null;
  entitlements: {
    tier: string;
    validUntil: string | null;
    paidUntil: string | null;
    quotas: Record<string, number>;
    /**
     * 配额键 → 中文名、单位、说明。
     *
     * **不要在前端手抄一份「键名 → 中文」。** 这些文案存在
     * `quota_default` 表里，和数值同一行，由服务器一起下发。
     * 手抄过一次，第一版就漏了四项，账号页上把 `pricerules`、
     * `dailysend` 这种键名直接显示给了用户。
     */
    quotaMeta: Record<string, { label: string; unit: string | null; note: string | null }>;
  };
}

export interface SettingsRead {
  settings: Record<string, unknown>;
  /**
   * 和默认值不同的那些项：**键 → 值的对象，不是键名数组。**
   *
   * 这里原来写的是 `string[]`，和服务端（packages/settings 的 readSettings）对不上，
   * 只是一直没人读它所以没出事。游客登录时第一次读了它（`.includes`），
   * 当场抛错，刚登录的人卡在「读取中」出不来。真浏览器里走一遍才抓到的。
   */
  overrides: Record<string, unknown>;
  /** 乐观锁版本号。保存时必须带上 */
  version: number;
  /** 因为依赖没满足而当前无效的键 */
  inactive: string[];
}

/**
 * `GET /api/public/plans` —— 价目表和两个档位的额度。**不要登录。**
 *
 * 首页、账号页、游客模式下的配额锁都读它。数字从数据库来
 * （`admin_param` 的定价、`quota_default` 的额度），前端一个都不写死 ——
 * 运营改了价格，首页不用发版就跟着变。
 */
export interface PublicPlans {
  prices: {
    /** 年付价格，美元。十进制字符串（金额一律不用浮点传） */
    yearUsd: string;
    monthUsd: string;
  };
  /**
   * 现在能不能真的买。**收款还没上线时是 false** ——
   * 界面上不许出现能点的「购买」按钮，只能说「即将开放」。
   */
  purchasable: boolean;
  /**
   * 能往哪条链的哪个币付。`purchasable` 是 false 时是空数组。
   *
   * 为什么不是「两条链两个币」写死：某个币的收款账号没配好时，
   * 让用户往那里转钱等于让钱进虚空 —— 所以能收哪个由服务器说。
   */
  payWith: Array<{ chain: 'bsc' | 'sol'; token: 'usdt' | 'usdc' }>;
  quotas: Array<{
    key: string;
    kind: 'count' | 'interval' | 'throughput';
    /** 服务器上只有中文名（quota_default 表）。首页另有三语标签，拿不到时退回这个 */
    label: string;
    unit: string | null;
    note: string | null;
    free: number;
    vip: number;
    /** 绝对边界。VIP 的值等于它时界面显示「不限」 */
    hardCap: number;
  }>;
}

/**
 * `GET /api/public/login-methods` —— 登录页能画出哪些按钮。**不要登录。**
 *
 * ════════════════════════════════════════════════════════
 * 为什么要问服务器，不在前端写死
 * ════════════════════════════════════════════════════════
 * 画一个没配好的按钮，用户点下去只会拿到一句看不懂的报错，
 * 而「按钮点了没反应」是这一块最难查的现象 —— 从界面上完全看不出
 * 是「后端没配 client secret」还是「BotFather 里没设域名」。
 *
 * Telegram 那一项给的是**机器人用户名**（公开信息，谁都能在 Telegram 里
 * 搜到它），不是 token。它的登录组件认的就是用户名。
 */
export interface LoginMethods {
  /** 邮箱验证码登录。2026-10-29 删了，恒为 false（字段留着给旧版前端） */
  email: boolean;
  /** 本地测试登录（只有本地开发服务器开着，线上恒为 false / 没有） */
  devLogin?: boolean;
  google: boolean;
  /**
   * 配了、**而且 Telegram 那边确认我们的域名能用**才有值。
   * BotFather 里没设域名时是 null（画出来会是一块写着 Bot domain invalid 的白框）
   */
  telegram: { botUsername: string } | null;
  /** 配了 Telegram 却是 null 的原因。给部署自检用，界面上不显示 */
  telegramIssue?: 'domain_not_set' | 'username_invalid' | 'checking' | null;
  /**
   * Discord 配好了没。**登录页不画它** ——
   * 它只用在「给已有账号再加一条入口」那张卡上。
   * 第一次来的人面前并排三个第三方按钮，等于让他先做一道
   * 「我以后要用哪个」的选择题，而选错了以后就是两个账号。
   */
  discordLinkable: boolean;
}

/**
 * `GET /api/me/logins` —— 现在能用哪些方式进这个账号。
 *
 * `ways` 是「一共有几条能进来的路」，前端用它决定解绑按钮能不能点。
 * **邮箱算一条路，但只有在它不是占位地址时才算** ——
 * Telegram 注册的账号 email 是 `tg-<id>@telegram.invalid`，那条路不通。
 */
export interface MyLogins {
  email: string;
  /** 是占位邮箱（Telegram 注册的账号）—— 收不到任何邮件 */
  emailIsPlaceholder: boolean;
  /** 邮箱能不能收信（2026-10-29 起不再是一条登录的路，只是显示用） */
  emailUsable: boolean;
  providers: Array<{ provider: string; addedAt: string }>;
  ways: number;
}

/** `GET /api/secrets` */
export interface SecretsRead {
  /** 已经填过的。**只有掩码** —— 明文我们自己也读不回来 */
  secrets: Array<{
    ref: string;
    /** 给人看的名字，比如「事项提醒 › Telegram 群通知 › Bot Token」 */
    label: string;
    mask: string;
    keyVersion: number;
    createdAt: string;
    updatedAt: string;
    lastUsedAt: string | null;
  }>;
  /** 还没填的凭据键 */
  missing: string[];
}

/**
 * `POST /api/notify/test` —— 设置页上「发送测试消息」那几个按钮。
 *
 * **发的是存好的设置**（服务器那边读库），不是界面上还没保存的草稿 ——
 * 所以界面要先拦住「有没保存的改动」，不然测的不是他眼前看到的那一份。
 */
export interface NotifyTestRead {
  ok: true;
  results: NotifyTestResult[];
  /** 一个通道都没开时的那句话（results 是空的） */
  empty?: string | undefined;
  /** 「测试提醒效果」：网页马上照这一种的样子弹出来（ext = 扩展在线，电脑上也会弹，网页就不再出声） */
  preview?: { kind: string; title: string; body: string; ext: boolean } | undefined;
}

export interface NotifyTestResult {
  channel: 'telegram' | 'bark' | 'feishu';
  /** 通道的名字（Telegram / Bark / 飞书） */
  label: string;
  /** 对方服务器收下了 */
  ok: boolean;
  /** 试着发了没有。没配齐、超出档位的不发 */
  tried: boolean;
  reason?: string | undefined;
  /** 这个通道的「启用」关着：单独测它照样发，但真的提醒不会走这里 */
  off?: boolean | undefined;
  /** 平台机器人发不出去，因为他还没在 Telegram 里点过「开始」 */
  needStart?: boolean | undefined;
  /** 打开机器人的链接（t.me） */
  startUrl?: string | undefined;
}

/** `GET /api/notify/telegram` —— 平台机器人的一键绑定现在是什么状态 */
export interface TelegramBindState {
  /** 平台机器人。null = 这台服务器没配，一键绑定用不了 */
  bot: { username: string } | null;
  /** Telegram 的登录组件在这个域名上画得出来 */
  widget: boolean;
  /** 账号上已经绑着 Telegram 登录（可以直接用那个号） */
  linked: boolean;
  mode: 'platform' | 'own';
  enabled: boolean;
  /** 平台模式下绑着的号。只有尾号 */
  bound: { tail: string; isLoginAccount: boolean } | null;
}

/** `POST /api/notify/telegram/bind` 成功时 */
export interface TelegramBindOk {
  ok: true;
  version: number;
  tail: string;
  /** 确认消息发到了 */
  delivered: boolean;
  reason?: string | undefined;
  /** 没发到，因为他还没在 Telegram 里点过机器人的「开始」 */
  needStart?: boolean | undefined;
  startUrl?: string | null | undefined;
}

/** `PATCH /api/settings` / `POST /api/settings/reset` 成功时的返回 */
export interface SettingsWriteOk {
  ok: true;
  /** 新的乐观锁版本号。**要存下来**，下一次保存得带上它 */
  version: number;
  changed: string[];
  /** 提交了但值没变的项数 */
  noop: number;
}

/** `GET /api/wallets` */
export interface WalletsRead {
  wallets: WalletRow[];
  /** 当前档位能盯几个 */
  limit: number;
  /** 已经开着的有几个。**关掉的不占额度** */
  used: number;
}

export interface WalletRow {
  id: string;
  chain: string;
  address: string;
  short: string;
  label: string;
  enabled: boolean;
  sortOrder: number;
  lastSnapshot: {
    totalRealized: string | null;
    todayRealized: string | null;
    unrealized: string | null;
    statsRealized: string | null;
    buyCount: number;
    sellCount: number;
    winrateBps: number;
  } | null;
  lastSnapshotAt: string | null;
  lastTradeCount: number;
  createdAt: string;
}

export interface Presence {
  sitting: boolean;
  sittingSeconds: number;
  lastBeatAt: string | null;
  /** 8 分钟没报到就算失联（扩展每 5 分钟报一次到，见 @jyfk/scheduler 的 EXTENSION_ALIVE_MS） */
  extensionAlive: boolean;
  clientVersion: string | null;
}

/** `GET /api/activities` */
export interface ActivitiesRead {
  activities: Activity[];
}

/* ══════════════════════ 总览 ══════════════════════ */

/**
 * `GET /api/overview`
 *
 * ════════════════════════════════════════════════════════
 * 聚合在服务端做。前端**一个数字都不自己算。**
 * ════════════════════════════════════════════════════════
 * 「今天」的边界（用户的时区 + 日结时刻）、胜率口径、bigint 求和
 * 三件事都在 `@jyfk/rules` 里，而那份代码将来是真的会拦人的判定。
 * 前端重算一遍必然漂移，于是界面上的今日盈亏和真正触发熔断的数字不一样。
 *
 * 金额全部是**微美元的十进制字符串**（字段名带 Micro 后缀），用 `money()` 显示。
 */
export interface Overview {
  /** 当前交易日编号 YYYY-MM-DD */
  todayKey: string;
  /** 「今天」是怎么算的 —— 界面上要写出来，不然用户搞不懂为什么凌晨两点还算昨天 */
  dayContext: { timezone: string; dayStartAt: string };

  /** 我们自己从成交流水算的今日数字，按用户的交易日 */
  today: {
    dayKey: string;
    /** 最近一小时开仓 + 平仓几笔（和交易频率那条纪律同一个数法）。老的服务器不给 */
    lastHourCount?: number;
    closedCount: number;
    openedCount: number;
    winCount: number;
    lossCount: number;
    netPnlMicro: string;
    feeUsdMicro: string;
    /** 今日有几笔卖出算不出成本，没进统计 */
    skippedNoCost: number;
  };

  /**
   * 上游（GMGN）快照的汇总。**和 `today` 不是一个口径。**
   *
   * 它的「今天」是 GMGN 的 1d 口径，和用户设的日结时刻无关。
   * 所以两个数字几乎一定不相等 —— 界面上必须分开显示、各自标明来源。
   * 合成一个就等于在两个口径里挑一个假装是真相。
   */
  upstream: {
    todayRealizedMicro: string | null;
    totalRealizedMicro: string | null;
    unrealizedMicro: string | null;
    walletsEnabled: number;
    walletsReporting: number;
    walletsNeverCollected: number;
    /** 最旧的一次采集时间 —— 整屏数据的新鲜度以它为准 */
    oldestSnapshotAt: string | null;
  };

  /** 统计窗口内的期望值。`grade === 'insufficient'` 时不要下结论 */
  stat: {
    ok: boolean;
    count: number;
    winCount: number;
    lossCount: number;
    /** 0–1 */
    winRate: number;
    rSampleCount: number;
    /** 算不出 R 的笔数。**要显示出来** */
    unusableCount: number;
    avgWinR: number;
    avgLossR: number;
    expectancyR: number;
    profitLossRatio: number | null;
    noLoss: boolean;
    breakevenWinRate: number | null;
    stdDevR: number;
    sqn: number | null;
    sqnReliable: boolean;
    totalNetPnlMicro: string;
    totalFeeMicro: string;
    maxDrawdownR: number;
    maxLossStreak: number;
    enoughSample: boolean;
    minSample: number;
    grade: 'insufficient' | 'bad' | 'breakeven' | 'good' | 'excellent';
    ratioMet: boolean;
  };

  window: {
    /** null = 全部记录 */
    days: number | null;
    fromDayKey: string | null;
    /** 撞到了条数上限，统计不完整 */
    truncated: boolean;
    earliestSeenAt: string | null;
    closedCount: number;
    skippedNoCost: number;
  };

  streak: {
    /** **只在统计窗口内数的**，可能少算 */
    loss: number;
    win: number;
    /** 连亏一直数到了窗口边界 → 界面要显示成「至少 N 笔」 */
    lossHitWindowEdge: boolean;
  };

  /** 窗口内每个有平仓的交易日，按日期正序。没有平仓的日子不在里面（不是 0） */
  daily: Array<{ dayKey: string; netPnlMicro: string; count: number; winCount: number }>;

  recent: Array<{
    key: string;
    at: string;
    symbol: string;
    walletId: string;
    tokenAddress: string;
    costUsdMicro: string;
    netPnlMicro: string;
    feeUsdMicro: string;
    /** null = 这一笔算不出 R（成本基准是 0 或拿不到） */
    rMultiple: number | null;
    riskBasis: 'stop' | 'cost' | 'none';
    isWin: boolean;
  }>;

  presence: {
    sitting: boolean;
    sittingSeconds: number;
    lastBeatAt: string | null;
    extensionAlive: boolean;
  };

  /** 规则判定有没有在跑。**总览页最重要的一条信息** —— 见 Guard */
  guard: Guard;

  /**
   * 总览常驻那一句心法。服务端按**交易日**（或你设的间隔）算，同一个周期里不变 ——
   * 前端自己从设置里随机挑的话，每次刷新都会换一句，那不是轮换是抽奖。
   */
  creedQuote: string;

  /** 纪律此刻要你做什么：熔断中吗、有没有待确认的提现、有哪些催办挂着 */
  discipline: Discipline;
  /** 钱包监控的总开关（设置 gm_enabled）。false 时判定不看钱包数据 */
  monitoring: boolean;
}

/**
 * 总览页上「纪律此刻要你做什么」那一块（`GET /api/overview` 的 `discipline`，
 * 以及 `POST /api/discipline/*` 做完之后交回来的那一份）。
 *
 * **规矩在服务端**：能不能提前解除、要等多久、要抄哪一句，界面只是照着画。
 * 绕过界面直接调接口也绕不过去。
 */
export interface Discipline {
  /** 有没有运行时状态（还没加过钱包的人没有 —— 他只能主动休息） */
  ready: boolean;
  /** 状态读不回来了。这时候纪律不会拦他，界面必须说出来 */
  broken: boolean;
  breaker: null | {
    reason: string;
    reasonText: string;
    /** 自己按下的（主动休息 / 手感休眠），不是纪律触发的 */
    selfImposed: boolean;
    startedAt: string;
    endsAt: string;
    toEndOfDay: boolean;
    unlock: {
      phrase: boolean;
      direct: boolean;
      /** 还要等多久才允许提前解除，毫秒（最少锁定） */
      waitMs: number;
      phraseText: string;
      minLockMinutes: number;
    };
  };
  withdrawals: Array<{
    id: string;
    walletLabel: string;
    gainUsdMicro: string;
    requiredUsdMicro: string;
    createdAt: string;
    deadlineAt: string;
  }>;
  nags: Array<{
    key: string;
    kind: string;
    kindText: string;
    repeats: number;
    nextAt: string;
    createdAt: string;
    walletLabel: string | null;
    winStreak: number | null;
  }>;
  today: { dayKey: string; violations: number; withdrawConfirmed: number };
  rest: { presets: number[]; hotHandPresets: number[] };
  /**
   * 每日核心心法。
   *
   * `quote` 是总览常驻那一句（**不受「到点提醒」开关影响** —— 那个开关管的是
   * 「到点打断我」，不是「总览上显示一句」）。
   * `pending` 是等你确认的那一条：要抄哪一句**由服务端定**，前端只是照着画。
   */
  creed: {
    quote: string;
    remindOn: boolean;
    today: { done: number; skipped: number };
    pending: null | {
      id: string;
      /** 你自己写的那句话，原样显示 —— 一个字都不翻、不转繁体 */
      text: string;
      typing: boolean;
      typeText: string;
      /** 抄写超时自动放行的时刻。null = 不限时 */
      timeoutAt: string | null;
      createdAt: string;
    };
  };
}

/**
 * 判定引擎的活着状态。
 *
 * 来自调度器的心跳（`scheduler_lease`）。`active` 为 false 时，
 * 用户配的 16 条纪律**一条都不会触发**：不会熔断、不会锁屏、不会发提醒。
 *
 * 界面上必须显著地说出来。不说的后果是最坏的一种：
 * 用户以为有人看着，于是真的放开手做单 ——
 * 一个「看起来在守护」的风控工具比没有风控工具危险。
 */
export interface Guard {
  active: boolean;
  /**
   * `ok`            心跳新鲜，在跑
   * `never_started` 从来没启动过（`scheduler_lease` 里连一行都没有）
   * `no_heartbeat`  启动过，但心跳过期了 —— 进程挂了，或者卡住了
   * `deploying`     心跳过期了，但我们正在部署（滚动重启，几十秒）。标记三分钟自己过期，过了还没恢复就变回 no_heartbeat
   */
  reason: 'ok' | 'never_started' | 'no_heartbeat' | 'deploying';
  rulesConfigured: number;
  /** 上一次心跳是多久以前，秒。null = 从来没启动过 */
  staleSeconds: number | null;
  lastTickUsers: number | null;
  lastTickMs: number | null;
  /** 调度器自己报的上一个错误。给用户解释「为什么我的纪律没生效」 */
  lastError: string | null;
}

/**
 * 价格轮询那一路活着吗。
 *
 * ════════════════════════════════════════════════════════
 * **和 `Guard` 是两件事，不能合并。**
 * ════════════════════════════════════════════════════════
 * 那个读的是纪律判定（租约 `main`），这个读的是价格轮询（租约 `price`）。
 * 两路是两个独立的租约，可以一个活一个死。
 *
 * 合成一个状态的后果：纪律判定活着就显示「一切正常」，
 * 而价格告警其实已经停了几小时 —— 用户配的到价提醒一条都不来，
 * 而他以为有人在看着。
 */
export interface PriceGuard {
  active: boolean;
  reason: 'ok' | 'never_started' | 'no_heartbeat' | 'deploying';
  staleSeconds: number | null;
  /** 全站去重后在盯多少个币。这个数直接决定单机还能撑多少人 */
  targets: number | null;
  lastTickMs: number | null;
  lastError: string | null;
}

/**
 * 事项提醒那一路活着吗（租约 `remind`）。第三路，和上面两路一样可以单独死掉。
 * 事项页要同时看它和 `Guard`：到点排程是这一路，真正发出去是纪律判定那一路（主循环）。
 */
export interface LoopGuard {
  active: boolean;
  reason: 'ok' | 'never_started' | 'no_heartbeat' | 'deploying';
  staleSeconds: number | null;
  lastTickMs: number | null;
  lastError: string | null;
}

/**
 * 链上收款那一路活着吗（租约 `pay`）。第四路。
 *
 * **付款页必须看它。** 它停了而用户照样能下单的话，那笔钱会静静躺在
 * 收款地址里、订单过期 —— 这是唯一一种我们造成用户直接损失的失败。
 *
 * `open` 是 false 表示付款根本没开放（那时 `active` 当然也是 false，
 * 但原因是「刻意没开」而不是「挂了」，界面上的说法完全不一样）。
 */
export interface PayGuard extends LoopGuard {
  open: boolean;
}

/* ══════════════════════ 收件箱 ══════════════════════ */

/**
 * `GET /api/inbox`
 *
 * ════════════════════════════════════════════════════════
 * 这一页回答的是「为什么没提醒我」
 * ════════════════════════════════════════════════════════
 * 数据源是发件箱（`notify_outbox`）：每一条提醒往哪几个通道发了、
 * 到了没到、没到是为什么。**判定全部在服务端**（`@jyfk/notify` 的
 * `outbox-view.ts`，纯函数），前端只负责按码取文案 ——
 * 同一种失败在列表上说一句话、在详情里说另一句话，比不说更糟。
 *
 * 服务端已经做过两件事，前端不要再做：
 *   上游原话**脱敏过了**（Bot Token、Device Key、会话号、邮箱）
 *   快照只给白名单那几样（`data` / `msg` 一律不下发）
 */
export interface InboxRead {
  /** 按时间倒序，最新的在前。一条提醒一项（几个通道归并好了） */
  items: InboxItem[];
  /** 下一页的游标。null = 没有更早的了。**不要自己拼这个串** */
  nextCursor: string | null;
  limit: number;
  /**
   * 筛选器上的计数。**按「投递」算** —— 一条提醒走两个通道算两条。
   * 界面上必须写明这件事，否则他会拿 37 去对列表里的 12 条。
   */
  counts: {
    total: number;
    sent: number;
    pending: number;
    failed: number;
    dropped: number;
    /** 最早的一条是什么时候。用来如实说「更早的按你的档位保留 N 天」 */
    oldestAtMs: number | null;
  };
  /**
   * 这个档位的历史保留天数（免费 30 / VIP 180）。
   * **现在还没有清理任务**，实际上一条都没删 —— 界面上按「保留 N 天」说，
   * 不要说「更早的已经删了」。
   */
  keepDays: number | null;
  /** 服务器此刻。「还有多久重试」要和服务器同一个时钟算，不能用浏览器的 */
  serverNow: string;
}

/** 一条提醒在收件箱里的样子 */
export interface InboxItem {
  /** 通知级的幂等键。列表的 key，也是「展开了哪一条」的标识 */
  key: string;
  kind: string;
  title: string;
  body: string;
  /** 点开之后去哪。指向我们自己的站点 */
  url: string | null;
  urlLabel: string | null;
  at: string;
  atMs: number;
  /** `passive` / `active` / `timeSensitive` / `critical` */
  urgency: string;
  /**
   * `delivered` 每一路都成功 / `partial` 至少一路成功（**算到了**）/
   * `inflight` 还在发 / `none` 一路都没成功而且不会再试
   */
  verdict: 'delivered' | 'partial' | 'inflight' | 'none';
  sentCount: number;
  total: number;
  /**
   * 紧急提醒只走通了一路。
   * 不能显示成绿色的「已送达」—— 双通道是下限，不是偏好。
   */
  weakCritical: boolean;
  deliveries: InboxDelivery[];
  /** 这一条在收件箱里处理过没有（确认 / 延后 / 已处理）。null = 还没处理 */
  triage?: { state: 'ack' | 'done' | 'snoozed'; untilMs: number | null } | null;
}

export interface InboxDelivery {
  id: string;
  /** `telegram` / `bark` / `feishu` / `email` */
  channel: string;
  urgency: string;
  /**
   * `sent` 已送达 / `queued` 排队中 / `sending` 正在发送 /
   * `retrying` 还会再试 / `failed` 送不出去 /
   * `canceled` 你点了「处理完了」之后取消的响铃 / `dropped` 没试就放弃了
   */
  state: 'sent' | 'queued' | 'sending' | 'retrying' | 'failed' | 'canceled' | 'dropped';
  /**
   * 没送到是谁的事。**这几类必须分清，不然文案一定会撒谎**：
   * `ours` 我们这边没做完（别让他去查自己的配置）/
   * `theirs` 他那边没配齐（给他能照着做的一句话）/
   * `upstream` 上游临时不行（说「还在重试」）/
   * `canceled` 他自己取消的 / `unknown` 认不出来（不猜）
   */
  cause: 'ours' | 'theirs' | 'upstream' | 'canceled' | 'dnd' | 'unknown' | null;
  attempts: number;
  willRetry: boolean;
  nextAttemptAtMs: number | null;
  sentAtMs: number | null;
  /** 上游原话（已脱敏）。放在我们自己那句话下面当「细节」 */
  detail: string | null;
  /** Bark「接着响」的第几条。null = 不是接着响的 */
  ringNo: number | null;
}

/** `GET /api/reminders` */
export interface RemindersRead {
  reminders: ReminderRow[];
  /** 当前档位能同时记几条没处理完的 */
  limit: number;
  /** 现在有几条没处理完。**处理完的不占额度** */
  used: number;
}

export interface ReminderRow {
  id: string;
  title: string;
  link: string;
  note: string;
  /** 这一次原定的到期时刻（ISO）。重复事项每结束一次往后滚 */
  remindAt: string;
  /** 「稍后再说」之后的到期时刻。null = 没推迟 */
  snoozedUntil: string | null;
  /** 这一次实际的到期时刻 = snoozedUntil ?? remindAt */
  dueAt: string;
  timezone: string;
  /** 提前多少分钟提醒，升序 */
  leads: number[];
  repeat: 'none' | 'daily' | 'weekly';
  done: boolean;
  doneAt: string | null;
  /** 下一个会叫你的时刻。null = 这一次已经结束了（不重复的事项催满了还没处理） */
  nextFireAt: string | null;
  nextKind: 'lead' | 'due' | 'nag' | null;
  nextLeadMin: number | null;
  /** 这一次已经催了几次 */
  nagCount: number;
  fireCount: number;
  lastFiredAt: string | null;
  createdAt: string;
}

/** 一键处理页看到的那一条（`GET /api/public/r/:token`）。不含用户信息 */
export interface ReminderOccurrence {
  title: string;
  note: string;
  link: string;
  timezone: string;
  repeat: 'none' | 'daily' | 'weekly';
  /** 这一次实际响（或者已经响过）的时刻：推迟过就是推迟后的；stale 的时候是链接说的那一次 */
  dueAt: string;
  /** 事项现在的到期时刻（stale 的时候和上面不一样） */
  currentDueAt: string;
  /** 推迟过的话，推迟之前原定的时刻；没推迟过是 null */
  snoozedFrom: string | null;
  nagCount: number;
  status: 'upcoming' | 'due' | 'nagging' | 'overdue' | 'done' | 'stale';
}

/** `GET /api/tokens` */
export interface TokensRead {
  tokens: TokenRow[];
  /** 当前档位能盯几个币 */
  limit: number;
  /** 已经开着的有几个。**关掉的不占额度** */
  used: number;
  ruleLimit: number;
  ruleUsed: number;
  /**
   * **实际生效的**检查间隔，秒。不是设置里存着的那个值 ——
   * 一个 VIP 到期之后设置里还留着 60，而实际按 300 跑。
   * 显示设置里那个值就是在骗他。
   */
  intervalSec: number;
  /** 每分钟消耗多少个「地址轮询位」。全站容量的计价单位 */
  slotsPerMinute: number;
  /**
   * 判定时**真的在用**的全局流动性门槛（微美元字符串）。规则上没单独设门槛时按它算。
   * 设置里没填过是出厂的一万美元；0 = 不设门槛
   */
  globalMinLiquidityUsd: string;
  /** 「立即检查一轮」两次之间至少隔几秒（manual 配额：免费 60、VIP 20） */
  checkEverySec: number;
  /** 上一次按「立即检查一轮」的时刻。没按过是 null */
  checkRequestedAt: string | null;
  /** 下一次能按的时刻。没按过是 null（= 现在就能按） */
  checkNextAt: string | null;
}

export interface TokenRow {
  id: string;
  chain: string;
  address: string;
  short: string;
  /** 上游给的符号。空串 = 还没查到过 */
  symbol: string;
  note: string;
  enabled: boolean;
  sortOrder: number;
  /* 金额一律是**微美元的十进制字符串**。见文件头那条规则 */
  lastPriceUsd: string | null;
  lastLiquidityUsd: string | null;
  lastMarketCapUsd: string | null;
  lastQuoteAt: string | null;
  /**
   * 上次去查但**查不到**的时刻。
   * 有值而且没有价格 = 地址大概填错了（或者这个币没有池子）——
   * 界面要据此区分「刚添加还没轮到」和「查了但根本查不到」。
   */
  lastMissAt: string | null;
  /** 他最近一次按「立即检查一轮」时，这个币被记上的时刻。没按过是 null */
  checkRequestedAt: string | null;
  createdAt: string;
  /** 这个币的告警规则，按创建先后排。可以有多条（各自的状态、各自提醒），至少一条 */
  rules: RuleRow[];
}

export interface RuleRow {
  id: string;
  upEnabled: boolean;
  upStepPercent: number;
  downEnabled: boolean;
  downStepPercent: number;
  anchorMode: string;
  cooldownMinutes: number;
  maxNotifications: number;
  minLiquidityUsd: string | null;
  targetAboveUsd: string | null;
  targetBelowUsd: string | null;
  mcAboveUsd: string | null;
  mcBelowUsd: string | null;
  /* ── 判定状态。界面上要显示，因为「为什么没提醒我」是最常见的疑问 ── */
  /** 基准价。null = 还没锚定（第一次见到这个币只锚定、不提醒） */
  anchorUsd: string | null;
  firedCount: number;
  lastFiredAt: string | null;
}

export interface Activity {
  id: string;
  walletId: string;
  txHash: string;
  eventType: string;
  tokenAddress: string;
  tokenSymbol: string;
  tokenAmount: string;
  /** 微美元的十进制字符串。**不是 number** —— 超过 2^53 会丢精度 */
  costUsdMicro: string;
  buyCostUsdMicro: string | null;
  gasUsdMicro: string;
  priceUsdRaw: string;
  happenedAt: string;
}

/** 一个付款订单（`GET /api/orders`、`POST /api/orders`） */
export interface OrderRow {
  id: string;
  chain: 'bsc' | 'sol';
  token: 'usdt' | 'usdc';
  status: 'pending' | 'confirming' | 'paid' | 'expired' | 'review' | 'canceled' | 'refunded';
  /** 中文状态（服务器下发，只有一份文案） */
  statusText: string;
  /** 还需要用户做点什么（只有「等你付款」是 true） */
  needsAction: boolean;
  /** 还有多久过期，毫秒。过期了是 0 */
  expiresInMs: number;
  months: number;
  /** 价目表上的价钱，两位小数 */
  nominalUsd: string;
  /**
   * **实际要转的金额，六位小数。** 带唯一尾数 —— 少一位就匹配不上，
   * 所以界面上必须让人一键复制，不能指望他手打。
   */
  amountUsd: string;
  /** 要粘进钱包 / 交易所提币界面的地址。Solana 上是主地址 */
  payTo: string | null;
  /** 二维码里那串字。地址没配齐时是 null */
  paymentUri: string | null;
  receivedUsd: string | null;
  txHash: string | null;
  /** 已经攒了几个确认（BSC 才有意义） */
  confirmations: number;
  /** 转人工的原因。有值的话界面上要原样显示给用户 */
  reviewReason: string | null;
  /** 他说过「我付了，没开通」的时刻。null = 没说过 */
  claimedAt: string | null;
  /** 运营核对过、关掉了这条认领（钱确实没到之类） */
  claimClosed: boolean;
  createdAt: string;
  expiresAt: string;
  paidAt: string | null;
}

/** `GET /api/orders` */
export interface OrdersRead {
  orders: OrderRow[];
  purchasable: boolean;
  payWith: Array<{ chain: 'bsc' | 'sol'; token: 'usdt' | 'usdc' }>;
}

/* ══════════════════════ 一键喊话小伙伴 ══════════════════════ */

/**
 * `GET /api/buddies`
 *
 * 一个小伙伴要**对方点过同意**（status 是 `active`）才喊得动。
 * 这是整个产品里唯一一处往别人手机上发东西，所以状态要在界面上写清楚 ——
 * 一个「等对方确认」的人看起来和能喊的人一样，会让用户以为自己有后援。
 */
export interface BuddiesRead {
  buddies: BuddyRow[];
  /** 喊话的响铃参数（从设置里读的，夹过合法范围） */
  call: {
    level: 'critical' | 'timeSensitive' | 'active';
    volume: number;
    sound: string;
    callSeconds: number;
    title: string;
    body: string;
    dailyCap: number;
  };
  maxBuddies: number;
  /** 服务器配了站点地址吗。没配就加不了人（推送里放不了「不再接收」的链接） */
  siteReady: boolean;
}

export interface BuddyRow {
  id: string;
  name: string;
  /** 对方收到的推送里「谁在喊你」写什么 */
  callerLabel: string;
  server: string;
  /** 对方 Device Key 的掩码。明文永远不回传 */
  keyMask: string;
  status: 'pending' | 'active' | 'declined' | 'opted_out';
  /** 自己这边的开关（暂时不喊他） */
  enabled: boolean;
  invitedAt: string | null;
  inviteCount: number;
  respondedAt: string | null;
  createdAt: string;
  /** 今天已经喊过几次（按你的时区算） */
  callsToday: number;
}

/** `POST /api/buddies/call` 的结果，一个小伙伴一条 */
export interface BuddyCallResult {
  id: string;
  name: string;
  ok: boolean;
  /** 没喊到的原因（今天到上限了、推送失败的说明） */
  reason?: string;
  /** 连响一共几条（含第一条） */
  rings?: number;
}

export interface BuddyCallOk {
  ok: boolean;
  okCount: number;
  results: BuddyCallResult[];
}

/** `GET /api/public/b/:token` —— 对方那一页（不用登录） */
export interface BuddyPeek {
  status: 'pending' | 'active' | 'declined' | 'opted_out';
  callerLabel: string;
  dailyCap: number;
}
